import { GridGenerator, Loading } from "@actinc/dls";
import React, { useEffect, useState } from "react";
import { ControlledFormInput } from "../../../components";
import { Box, CardContent, Typography } from "@mui/material";
import { useForm } from "react-hook-form";
import { useParams, useLocation } from "react-router-dom";
import { useClient } from "urql";
import { getPassageQuery } from "../../../stores/passage";
import { getItemQuery } from "../../../stores/item";
import { getTasksByObjects } from "../../../services/utils";

const TaskDetails = () => {
  const { objectType, objectId, taskId } = useParams();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const program = queryParams.get("program");
  const client = useClient();
  const [task, setTask] = useState(null);
  const [object, setObject] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTaskDetails = async () => {
      const query = objectType === "item" ? getItemQuery : getPassageQuery;
      const objectData = await client
        .query(query, { id: objectId })
        .toPromise();
      const tasksData = await getTasksByObjects(client)({
        id: objectId,
        type: objectType,
      });
      const task = tasksData.find((t) => t.id === taskId);
      setObject(objectData.data[`${objectType}ByID`]);
      setTask(task);
      setLoading(false);
    };
    fetchTaskDetails();
  }, [client, objectId, taskId, objectType]);

  const { control, reset } = useForm({ mode: "all" });

  useEffect(() => {
    if (task) {
      reset({
        program: program,
        contentArea: object?.contentArea,
        name: object?.name,
        itemFormat: object?.format,
        gradeLevel: object?.gradeLevel,
        requestedDateTime: new Date(task?.requestedDateTime),
      });
    }
  }, [task, object, reset, program]);

  if (loading) {
    return <Loading />;
  }

  if (!task) {
    return <Typography>Task not found</Typography>;
  }

  return (
    <form id="task-details-dialog-form">
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ mb: 2 }}>
          Task Details
        </Typography>

        <GridGenerator
          groups={[
            [
              {
                children: (
                  <ControlledFormInput
                    control={control}
                    readonly
                    fullWidth
                    id="program"
                    label="Program"
                    name="program"
                  />
                ),
                key: "task-details-program",
              },
              {
                children: (
                  <ControlledFormInput
                    control={control}
                    readonly
                    fullWidth
                    id="contentArea"
                    label="Content Area"
                    name="contentArea"
                  />
                ),
                key: "task-details-content-area",
              },
            ],
            [
              {
                children: (
                  <ControlledFormInput
                    control={control}
                    readonly
                    fullWidth
                    id="name"
                    label={`${objectType} Name`}
                    name="name"
                  />
                ),
                key: "task-details-item-name",
              },
              {
                children: objectType === "item" && (
                  <ControlledFormInput
                    control={control}
                    readonly
                    fullWidth
                    id="itemFormat"
                    label="Item Format"
                    name="itemFormat"
                  />
                ),
                key: "task-details-item-format",
              },
            ],
            [
              {
                children: (
                  <ControlledFormInput
                    control={control}
                    readonly
                    fullWidth
                    id="gradeLevel"
                    label="Grade"
                    name="gradeLevel"
                  />
                ),
                key: "task-details-gradeLevel",
              },
              {
                children: (
                  <ControlledFormInput
                    control={control}
                    readonly
                    fullWidth
                    id="requestedDateTime"
                    label="Request Date"
                    name="requestedDateTime"
                  />
                ),
                key: "task-details-requested-date-time",
              },
            ],
          ]}
        />

        {task.imageDetails?.map((image, i) => (
          <CardContent
            key={i}
            sx={{
              border: "1px dashed grey",
              borderRadius: 2,
              mt: 3,
              p: 2,
              backgroundColor: "#f9f9f9",
            }}
          >
            <Typography variant="h6" sx={{ color: "primary.main", mb: 1 }}>
              Component: {image.component}
            </Typography>

            <GridGenerator
              groups={[
                [
                  {
                    children: image && image.file && (
                      <>
                        <CardContent>
                          <img
                            alt="task-details-preview"
                            src={`/${image.file}`}
                          />
                        </CardContent>
                      </>
                    ),
                    key: `task-details-image-preview-${i}`,
                  },
                ],
                [
                  {
                    children: (
                      <Box sx={{ ml: 2 }}>
                        <Typography variant="h6">Art Type</Typography>
                        <Typography variant="body1">{image.type}</Typography>
                      </Box>
                    ),
                    key: `task-details-request-type-${i}`,
                  },
                  {
                    children: (
                      <Box>
                        <Typography variant="h6">Color Format</Typography>
                        <Typography variant="body1">{image.format}</Typography>
                      </Box>
                    ),
                    key: `task-details-color-format-${i}`,
                  },
                ],
                [
                  {
                    children: (
                      <Box sx={{ ml: 1 }}>
                        <Typography variant="h6">Description</Typography>
                        <Typography
                          variant="body1"
                          dangerouslySetInnerHTML={{
                            __html: image.description,
                          }}
                        />
                      </Box>
                    ),
                    key: `task-details-request-description-${i}`,
                  },
                ],
              ]}
            />
          </CardContent>
        ))}
      </Box>
    </form>
  );
};

export { TaskDetails };
